import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface LoginProps {
  onNavigate: (page: string) => void;
}

function LogoMark() {
  return (
    <div className="flex items-center gap-[11px]">
      <span className="w-[13px] h-[13px] rounded-full grad-accent-simple" />
      <span className="font-display font-extrabold tracking-[0.16em] text-white text-[17px]">
        AUDIARO
      </span>
    </div>
  );
}

function BrandPanel() {
  return (
    <div
      className="relative hidden lg:flex flex-col justify-between overflow-hidden px-[60px] py-14"
      style={{
        background:
          'radial-gradient(120% 90% at 20% 0%, rgba(var(--a-rgb),.16) 0%, rgba(12,12,14,0) 55%), #0C0C0E',
      }}
    >
      <LogoMark />
      <div>
        <div
          className="font-display font-extrabold uppercase tracking-[0.16em] text-xs mb-5"
          style={{ color: 'var(--a-core)' }}
        >
          Text to studio audio
        </div>
        <h1 className="font-display font-extrabold text-white text-[52px] leading-[1.05] tracking-[-0.01em] m-0">
          Your words,
          <br />
          in a voice
          <br />
          <span className="text-grad">worth hearing.</span>
        </h1>
        <p className="text-mist-400 text-base leading-[1.7] max-w-[400px] mt-[22px] mb-0">
          Convert any script into natural, broadcast-ready audio with 120+ AI voices across Murf,
          ElevenLabs and OpenAI &mdash; in seconds.
        </p>
      </div>
      <div className="flex items-center gap-3.5 px-5 py-[18px] border border-ink-700 rounded-[14px] max-w-[440px] bg-[rgba(23,23,27,.6)]">
        <span className="w-11 h-11 rounded-full flex-none flex items-center justify-center font-display font-extrabold text-ink-950 grad-accent-simple">
          CV
        </span>
        <div>
          <p className="m-0 text-mist-200 text-sm leading-normal">
            &ldquo;We produce a week of podcast promos in one afternoon. Audiaro paid for itself day
            one.&rdquo;
          </p>
          <p className="mt-1.5 mb-0 text-mist-500 text-[12.5px]">
            Carlos V. &mdash; Funnel Master Consultant
          </p>
        </div>
      </div>
    </div>
  );
}

export function Login({ onNavigate }: LoginProps) {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    if (!result.success) {
      setError(result.error || 'Login failed');
    } else {
      onNavigate('dashboard');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] bg-ink-950">
      <BrandPanel />

      <div className="flex items-center justify-center p-6 sm:p-10 bg-ink-900">
        <div className="w-full max-w-[380px] fx">
          <div className="lg:hidden mb-10">
            <LogoMark />
          </div>

          <h2 className="font-display font-extrabold text-white text-[28px] mt-0 mb-1.5">
            Welcome back
          </h2>
          <p className="text-mist-400 text-sm mt-0 mb-7">Sign in to your Audiaro studio.</p>

          <form onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-[11px] px-4 py-3 mb-[18px] text-status-danger text-[13px] bg-[rgba(216,54,47,.12)]">
                {error}
              </div>
            )}

            <label htmlFor="email" className="label">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input mb-[18px]"
              placeholder="you@example.com"
            />

            <label htmlFor="password" className="label">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input mb-3.5"
              placeholder="Enter your password"
            />

            <div className="flex items-center justify-between mb-6">
              <label
                htmlFor="remember-me"
                className="flex items-center gap-2 text-mist-500 text-[13px] cursor-pointer"
              >
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 rounded border-ink-600 bg-ink-950 accent-[var(--a-core)]"
                />
                Remember me
              </label>
              <a href="#" className="text-[13px] no-underline" style={{ color: 'var(--a-link)' }}>
                Forgot password?
              </a>
            </div>

            <button type="submit" disabled={loading} className="btn-grad w-full py-3.5">
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <p className="text-center text-mist-500 text-[13.5px] mt-[22px] mb-0">
            New here?{' '}
            <button
              onClick={() => onNavigate('register')}
              className="font-semibold bg-transparent border-0 p-0 cursor-pointer"
              style={{ color: 'var(--a-link)' }}
            >
              Create an account
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
